import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useWizard } from "@/lib/wizard-context";
import { useTenant } from "@/lib/tenant-context";
import { useAuth } from "@/lib/auth-context";
import CardMockPreview from "@/components/CardMockPreview";
import WizardProgress from "@/components/WizardProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Check, Send, Users, CreditCard, MapPin, Loader2, Gift, Copy, Share2, Mail, Lock, Eye, EyeOff, Building2 } from "lucide-react";
import { CARD_COLORS } from "@/lib/mock-data";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { cardRequestService, waitlistService } from "@/services";
import { supabase } from "@/integrations/supabase/client";

const SubmissionFlow = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, branches } = useTenant();
  const { user, signIn, signUp, signInWithMagicLink } = useAuth();
  const { toast } = useToast();
  const {
    config, setConfig, orientation, setOrientation,
    avatarPosition, setAvatarPosition, avatarScale, setAvatarScale,
    avatarVariants, selectedAvatarId, setSelectedAvatarId,
    firstName, setFirstName, lastName, setLastName,
    panLast5, setPanLast5,
    deliveryType, setDeliveryType,
    branchId, setBranchId,
    postalAddress, setPostalAddress,
    fundingAmountCents, setFundingAmountCents,
    cutoutUrls, useCutout,
    backgroundId, backgroundUrl,
  } = useWizard();

  const isWaitlist = tenant?.tenantType === "waitlist";
  const STEPS = useMemo(() => (
    isWaitlist
      ? ["Your Name", "Fund Card", "Delivery", "Preview", "Save Card", "Join Waitlist"]
      : ["Your Name", "Card Details", "Delivery", "Preview", "Submit"]
  ), [isWaitlist]);

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [myReferralCode, setMyReferralCode] = useState<string | null>(null);
  
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);
  const [setupIntentDone, setSetupIntentDone] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  // Stripe Checkout session id, retained so the server-side verifier can be
  // re-run after the waitlist row exists (payment_method_captured is server-only).
  const [setupSessionId, setSetupSessionId] = useState<string | null>(null);

  const [draftCardRequestId, setDraftCardRequestId] = useState<string | null>(null);
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvc, setCardCvc] = useState("");
  // Auth state for inline sign-up/sign-in
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const handledStripeReturnRef = useRef<string | null>(null);

  // Pre-fill referral code from URL param
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  // Auto-discover existing draft card request for this user+tenant
  useEffect(() => {
    if (!user || !tenant || draftCardRequestId) return;
    const findDraft = async () => {
      // Check localStorage first
      const savedDraftId = localStorage.getItem("cardmoji_draft_card_id");
      if (savedDraftId) {
        const { data: draft } = await supabase
          .from("card_requests")
          .select("id, user_id, tenant_id, status, first_name, last_name, funding_amount_cents, postal_address, card_color, style, style_sub_choice, hobby, orientation, selected_avatar_image_id, avatar_position, avatar_scale")
          .eq("id", savedDraftId)
          .eq("user_id", user.id)
          .eq("tenant_id", tenant.id)
          .eq("status", "draft")
          .maybeSingle();
        if (draft) {
          setDraftCardRequestId(draft.id);
          if (draft.first_name) setFirstName(draft.first_name);
          if (draft.last_name) setLastName(draft.last_name);
          if (draft.funding_amount_cents) setFundingAmountCents(draft.funding_amount_cents);
          if (draft.postal_address) {
            const addr = draft.postal_address as any;
            if (addr.line1) setPostalAddress(addr);
          }
          // Restore design fields into wizard context
          if (draft.card_color) setConfig(prev => ({ ...prev, color: { id: "restored", name: "restored", hsl: draft.card_color } }));
          if (draft.style) setConfig(prev => ({ ...prev, style: draft.style }));
          if (draft.style_sub_choice) setConfig(prev => ({ ...prev, styleSubChoice: draft.style_sub_choice }));
          if (draft.hobby) setConfig(prev => ({ ...prev, hobby: draft.hobby as any }));
          if (draft.orientation) setOrientation(draft.orientation as "portrait" | "landscape");
          if (draft.selected_avatar_image_id) setSelectedAvatarId(draft.selected_avatar_image_id);
          if (draft.avatar_position) setAvatarPosition(draft.avatar_position as any);
          if (draft.avatar_scale) setAvatarScale(draft.avatar_scale);
          return;
        }
      }
      // Fallback: query for any draft belonging to this user+tenant
      const { data: draft } = await supabase
        .from("card_requests")
        .select("id, first_name, last_name, funding_amount_cents, postal_address")
        .eq("user_id", user.id)
        .eq("tenant_id", tenant.id)
        .eq("status", "draft")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (draft) {
        setDraftCardRequestId(draft.id);
        if (draft.first_name) setFirstName(draft.first_name);
        if (draft.last_name) setLastName(draft.last_name);
        if (draft.funding_amount_cents) setFundingAmountCents(draft.funding_amount_cents);
        if (draft.postal_address) {
          const addr = draft.postal_address as any;
          if (addr.line1) setPostalAddress(addr);
        }
      }
    };
    findDraft();
  }, [user, tenant, draftCardRequestId]);

  const selectedAvatar = avatarVariants.find((v) => v.id === selectedAvatarId);
  const cardColor = config.color || CARD_COLORS[0];

  /*
    NO FALLBACK PRICE. A missing configured fee must fail loudly, never render an
    invented figure — a default silently sold a card at a fraction of its price.
    `null` means "not configured": the money steps refuse instead of guessing.
  */
  const cardFeeCents = tenant?.cardFeeCents ?? null;
  const feeUnavailable = cardFeeCents === null;
  const feeNotConfigured = (
    <div className="max-w-lg mx-auto rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
      <p className="font-semibold">Pricing isn't available right now</p>
      <p className="mt-1 text-muted-foreground">
        This card programme has no fee configured, so we can't show you a price or take an order.
        Please try again later.
      </p>
    </div>
  );
  const fundingOptions = tenant?.fundingAmountsCents ?? [2500, 5000, 10000, 25000];

  // Handle checkout return once tenant context is ready
  useEffect(() => {
    const checkoutStatus = searchParams.get("checkout");
    if (checkoutStatus === "success") {
      setSubmitted(true);
      toast({ title: "Payment Successful! 🎉", description: "Your card has been ordered and will be sent to you soon." });
      return;
    }
    if (checkoutStatus === "cancelled") {
      toast({ title: "Payment Cancelled", description: "You can try again when you're ready.", variant: "default" });
      return;
    }

    const setupStatus = searchParams.get("setup");
    const sessionId = searchParams.get("session_id");
    if (!setupStatus) return;

    const handledKey = `${setupStatus}:${sessionId || "no-session"}:${tenant?.id || "no-tenant"}`;
    if (handledStripeReturnRef.current === handledKey) return;

    const saveCardIndex = STEPS.indexOf("Save Card");
    if (saveCardIndex >= 0) {
      setStep(saveCardIndex);
    }

    if (setupStatus === "cancelled") {
      handledStripeReturnRef.current = handledKey;
      toast({ title: "Setup Cancelled", description: "You can save your card when you're ready.", variant: "default" });
      return;
    }

    if (setupStatus === "success" && sessionId && tenant) {
      handledStripeReturnRef.current = handledKey;
      const draftId = searchParams.get("draft_id") || localStorage.getItem("cardmoji_draft_card_id");
      const verifySetup = async () => {
        try {
          const { data, error } = await supabase.functions.invoke("verify-setup-intent", {
            body: { sessionId, tenantId: tenant.id },
          });
          if (!error && data?.setupIntentId) {
            setSetupIntentId(data.setupIntentId);
            setSetupSessionId(sessionId);

            setSetupIntentDone(true);
            toast({ title: "Card Saved! 💳", description: "Your payment method has been securely saved." });
          }
        } catch (err) {
          console.error("verify-setup-intent error:", err);
        }

        // Restore wizard state from the draft card request saved before Stripe redirect
        if (draftId) {
          setDraftCardRequestId(draftId);
          const { data: draft } = await supabase
            .from("card_requests")
            .select("*")
            .eq("id", draftId)
            .eq("user_id", user.id)
            .eq("tenant_id", tenant.id)
            .maybeSingle();
          if (draft) {
            if (draft.first_name) setFirstName(draft.first_name);
            if (draft.last_name) setLastName(draft.last_name);
            if (draft.funding_amount_cents) setFundingAmountCents(draft.funding_amount_cents);
            if (draft.postal_address) {
              const addr = draft.postal_address as any;
              if (addr.line1) setPostalAddress(addr);
            }
          }
        }
      };
      verifySetup();
    }
  }, [searchParams, tenant, STEPS, toast]);

  const canProceed = () => {
    const stepName = STEPS[step];
    if (stepName === "Your Name") return firstName.trim() && lastName.trim();
    if (stepName === "Card Details") return panLast5.trim().length >= (tenant?.panDigitsRequired ?? 4);
    if (stepName === "Fund Card") return fundingAmountCents !== null;
    if (stepName === "Delivery") {
      if (deliveryType === "branch") return !!branchId?.trim();
      return !!(postalAddress.line1 && postalAddress.city && postalAddress.postalCode && postalAddress.country);
    }
    return true;
  };

  const formatCents = (cents: number) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
  };


  const handleSubmit = async () => {
    if (!tenant || !user) {
      toast({ title: "Not signed in", description: "Please sign in to continue.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      if (isWaitlist) {
        // Resolve referral code to entry ID
        let referredById: string | undefined;
        if (referralCode.trim()) {
          const { data: referrer } = await waitlistService.getEntryByReferralCode(tenant.id, referralCode.trim());
          if (referrer && referrer.user_id !== user?.id) {
            referredById = referrer.id;
          }
        }

        // Use existing draft (created before Stripe redirect) or create new
        let cardReq: any = null;
        let cardErr: any = null;
        if (draftCardRequestId) {
          // Update the existing draft to 'waitlisted' — only update status,
          // design fields are already saved in the draft by handleSaveCard
          const res = await supabase
            .from("card_requests")
            .update({
              status: "waitlisted",
            } as any)
            .eq("id", draftCardRequestId)
            .eq("user_id", user.id)
            .eq("tenant_id", tenant.id)
            .select()
            .maybeSingle();
          cardReq = res.data;
          cardErr = res.error;
        } else {
          // No draft exists — create fresh
          const res = await cardRequestService.createClientCardRequest(tenant.id, "waitlisted", {
            first_name: firstName,
            last_name: lastName,
            card_color: cardColor.hsl,
            style: config.style || "cartoon",
            style_sub_choice: config.styleSubChoice || null,
            hobby: config.hobby || null,
            orientation,
            selected_avatar_image_id: selectedAvatarId || null,
            avatar_position: avatarPosition,
            avatar_scale: avatarScale,
            delivery_type: "postal",
            postal_address: postalAddress.line1 ? postalAddress : null,
            funding_amount_cents: fundingAmountCents,
            background_id: backgroundId,
          });
          cardReq = res.id ? ({ id: res.id } as any) : null;
          cardErr = res.error;
        }

        if (cardErr) {
          console.error("Card request creation error:", cardErr);
          toast({ title: "Failed to save card request", description: cardErr.message, variant: "destructive" });
          setSubmitting(false);
          return;
        }

        // Link the user's recent completed avatar generations to this card request
        // so admin review can find them all (not just the cycle that produced the
        // selected avatar). Sweeps unlinked, completed rows from the last 24h.
        if (cardReq?.id) {
          const { error: linkErr } = await cardRequestService.linkMyAvatarGenerations(cardReq.id);
          if (linkErr) {
            console.error("Failed to link avatar_generations to card_request:", linkErr);
          }
        }

        const { data, error } = await waitlistService.joinWaitlist({
          tenant_id: tenant.id,
          user_id: user.id,
          email: user.email || "",
          first_name: firstName,
          last_name: lastName,
          referred_by: referredById,
          card_request_id: cardReq?.id,
        });

        await supabase
          .from("profiles")
          .update({ first_name: firstName, last_name: lastName } as any)
          .eq("user_id", user.id);

        // payment_method_captured / stripe_setup_intent_id are server-controlled
        // (guard_waitlist_privileged_columns pins them). Re-run the verifier so the
        // service-role path re-asserts them against the row that now exists.
        if (setupSessionId) {
          await supabase.functions.invoke("verify-setup-intent", {
            body: { sessionId: setupSessionId, tenantId: tenant.id },
          });
        }


        if (error) {
          const isDuplicate = error.message?.includes("duplicate") || error.code === "23505";
          if (isDuplicate) {
            // User already on waitlist — link the new card request to the existing entry
            const { data: existingEntry } = await waitlistService.getUserWaitlistEntry(tenant.id, user?.id || "");
            if (existingEntry && cardReq?.id) {
              await supabase
                .from("waitlist_entries")
                .update({
                  card_request_id: cardReq.id,
                  first_name: firstName,
                  last_name: lastName,
                } as any)
                .eq("id", existingEntry.id);
            }
            toast({ title: "You're on the list! 🎉", description: "Your card design has been updated." });
            setWaitlistPosition(existingEntry?.position || null);
            setMyReferralCode(existingEntry?.referral_code || null);
            setSubmitted(true);
          } else {
            toast({ title: "Failed to join", description: "Something went wrong. Please try again.", variant: "destructive" });
          }
          setSubmitting(false);
          return;
        }


        setWaitlistPosition(data?.position || null);
        setMyReferralCode((data as any)?.referral_code || null);
        setSubmitted(true);
        toast({ title: "You're on the list! 🎉", description: `You're #${data?.position || "?"} on the waitlist.` });

        supabase.functions.invoke("send-waitlist-notification", {
          body: {
            tenantId: tenant.id,
            userId: user?.id,
            email: user?.email,
            firstName,
            position: data?.position,
          },
        })
          .then(({ data: res, error }) => {
            // Fire-and-forget for the user, but never silent for us.
            if (error) console.error("send-waitlist-notification failed:", error);
            else if (res && (res as any).success === false) console.error("send-waitlist-notification rejected:", res);
          })
          .catch(console.error);
      } else {
        // For bank tenants, this shouldn't be called via handleSubmit
        return;
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  // Bank tenant: simple submit for admin review (no payment)
  const handleBankSubmit = async () => {
    if (!tenant) return;
    if (!user) {
      toast({
        title: "Not signed in",
        description: "Please sign in to submit your application.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const { id: submittedId, error: cardErr } = await cardRequestService.createClientCardRequest(
        tenant.id,
        "pending",
        {
          first_name: firstName,
          last_name: lastName,
          card_color: cardColor.hsl,
          style: config.style || "cartoon",
          style_sub_choice: config.styleSubChoice || null,
          hobby: config.hobby || null,
          orientation,
          selected_avatar_image_id: selectedAvatarId || null,
          avatar_position: avatarPosition,
          avatar_scale: avatarScale,
          delivery_type: deliveryType,
          branch_id: branchId || null,
          postal_address: deliveryType === "postal" && postalAddress.line1 ? postalAddress : null,
          pan_last5: panLast5 || null,
          background_id: backgroundId,
        }
      );
      const insertData = submittedId ? { id: submittedId } : null;

      if (cardErr) {
        const isDuplicate = cardErr.message?.includes("duplicate") || cardErr.code === "23505";
        if (isDuplicate) {
          toast({ title: "Already submitted!", description: "You already have a card request pending.", variant: "default" });
        } else {
          console.error("Card request error:", cardErr);
          toast({
            title: "Failed to submit",
            description: cardErr.message || "Something went wrong. Please try again.",
            variant: "destructive",
          });
        }
        setSubmitting(false);
        return;
      }

      // Link the user's recent completed avatar generations to this card request so
      // the admin review page can find them all (not just the cycle that produced
      // the selected avatar). Only sweeps unlinked, completed rows from the last 24h
      // to avoid pulling in unrelated sessions.
      if (insertData?.id) {
        const { error: linkErr } = await cardRequestService.linkMyAvatarGenerations(insertData.id);
        if (linkErr) {
          // Non-fatal — submission already saved. Log and move on.
          console.error("Failed to link avatar_generations to card_request:", linkErr);
        }
      }

      await supabase
        .from("profiles")
        .update({ first_name: firstName, last_name: lastName } as any)
        .eq("user_id", user.id);


      // Notify the tenant admin of the new submission. Fire-and-forget — the
      // user's submission is already successful; we don't want to block UI on email.
      if (insertData?.id) {
        supabase.functions
          .invoke("send-notification", {
            body: {
              type: "new_submission",
              cardRequestId: insertData.id,
              tenantId: tenant.id,
            },
          })
          .catch((e) => console.error("send-notification (new_submission) failed:", e));
      }

      setSubmitted(true);
      toast({ title: "Card Submitted! 🎉", description: "Your card design has been sent for review." });
    } catch (err) {
      console.error("Bank submit error:", err);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 text-center animate-fade-in">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-success/10 mb-6">
          {isWaitlist ? <Users className="h-10 w-10 text-success" /> : <Check className="h-10 w-10 text-success" />}
        </div>
        <h1 className="text-2xl font-bold font-display mb-2">
          {isWaitlist ? "You're on the Waitlist! 🎉" : "Card Submitted! 🎉"}
        </h1>
        <p className="text-muted-foreground mb-6 max-w-sm">
          {isWaitlist
            ? `You're #${waitlistPosition || "?"} on the list. We'll notify you when we reach our goal and your card goes into production!`
            : "Your card design has been submitted for review. We'll notify you once it's approved and your card is on its way!"}
        </p>
        {isWaitlist && (
          <div className="mb-4">
            <CardMockPreview
              cardColorHsl={cardColor.hsl}
              orientation={orientation}
              cardLayout={tenant?.cardLayout}
              avatarUrl={useCutout && cutoutUrls[selectedAvatarId || "photo"] ? cutoutUrls[selectedAvatarId || "photo"] : selectedAvatar?.imageUrl}
              isCutout={useCutout && !!cutoutUrls[selectedAvatarId || "photo"]}
              avatarPosition={avatarPosition}
              avatarScale={avatarScale}
              firstName={firstName}
              lastName={lastName}
              tenantName={tenant?.brandName}
              cardNetwork={tenant?.cardNetwork} showChip={tenant?.showChip} showCardNetwork={tenant?.showCardNetwork}
              backgroundUrl={backgroundUrl}
              className="max-w-[200px] mx-auto"
            />
          </div>
        )}
        /* LEGACY WAITLIST FORM. The "$5" here advertised a payout scheme that
           has never existed at any point — no balance, no PayPal, no cash. The
           real reward is a percentage off the Cardmoji card fee, granted only by
           `program_referral_settings`, and it is NEVER typed into copy. Queue
           position is the only claim this screen can make. */
        {/* Referral share card */}
        {isWaitlist && myReferralCode && (
          <div className="w-full max-w-sm mx-auto rounded-xl border bg-card p-5 shadow-card space-y-3 mb-4">
            <div className="flex items-center justify-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-sm">Share your code</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Share your code with friends. For every person who joins, you move up the waitlist.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg bg-muted px-4 py-2.5 text-center font-mono font-bold text-lg tracking-widest">
                {myReferralCode}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(myReferralCode);
                  toast({ title: "Copied!", description: "Referral code copied to clipboard." });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={() => {
                const shareUrl = `${window.location.origin}/t/${tenant_slug}?ref=${myReferralCode}`;
                if (navigator.share) {
                  navigator.share({ title: `Join ${tenant?.brandName}`, text: `Use my code ${myReferralCode} to join!`, url: shareUrl });
                } else {
                  navigator.clipboard.writeText(shareUrl);
                  toast({ title: "Link copied!", description: "Share link copied to clipboard." });
                }
              }}
            >
              <Share2 className="h-4 w-4" /> Share Link
            </Button>
          </div>
        )}
        {isWaitlist ? (
          <Button variant="outline" onClick={() => navigate(`/t/${tenant_slug}/account`)}>
            Go to My Account
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate(`/t/${tenant_slug}`)}>
            Back to Home
          </Button>
        )}
      </div>
    );
  }

  const renderStep = () => {
    const stepName = STEPS[step];
    switch (stepName) {
      case "Your Name": return renderNameStep();
      case "Card Details": return renderCardDetailsStep();
      case "Fund Card": return renderFundingStep();
      case "Delivery": return renderDeliveryStep();
      case "Preview": return renderPreviewStep();
      case "Save Card": return renderSaveCardStep();
      
      case "Join Waitlist": return renderSubmitStep();
      case "Submit": return renderBankSubmitStep();
    }
  };

  const panDigits = tenant?.panDigitsRequired ?? 4;

  const renderCardDetailsStep = () => (
    <Card className="max-w-lg mx-auto shadow-card">
      <CardHeader>
        <CardTitle className="text-lg">Card Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Enter the last {panDigits} digits of your PAN (card number). This helps the bank match your design to your account.</p>
        <div className="space-y-1.5">
          <Label htmlFor="panLast5">Last {panDigits} digits of PAN</Label>
          <Input
            id="panLast5"
            value={panLast5}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, panDigits);
              setPanLast5(val);
            }}
            placeholder={"0".repeat(panDigits)}
            maxLength={panDigits}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderNameStep = () => (
    <Card className="max-w-lg mx-auto shadow-card">
      <CardHeader>
        <CardTitle className="text-lg">Your Name</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">This will appear on your card.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName">First Name</Label>
            <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alex" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName">Last Name</Label>
            <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Johnson" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderFundingStep = () => {
    if (feeUnavailable) return feeNotConfigured;
    const fee = cardFeeCents as number;
    return (
    <div className="max-w-lg mx-auto space-y-4">
      <h2 className="text-xl font-bold font-display">Fund Your Card</h2>
      <p className="text-sm text-muted-foreground">Choose how much to load onto your new card.</p>

      {/* Card fee notice */}
      {fee > 0 && (
        <div className="flex items-center gap-3 rounded-xl bg-muted/50 px-4 py-3">
          <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-sm font-medium">Card issuance fee: {formatCents(fee)}</p>
            <p className="text-xs text-muted-foreground">This one-time fee covers the cost of your personalised card</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {fundingOptions.map((amount) => (
          <button
            key={amount}
            className={cn(
              "flex flex-col items-center gap-1 rounded-xl border bg-card p-5 transition-all shadow-card hover:shadow-elevated",
              fundingAmountCents === amount && "ring-2 ring-primary border-primary"
            )}
            onClick={() => setFundingAmountCents(amount)}
          >
            <span className="text-xl font-bold font-display">{formatCents(amount)}</span>
            <span className="text-xs text-muted-foreground">Card balance</span>
          </button>
        ))}
      </div>

      {fundingAmountCents !== null && (
        <div className="rounded-xl border bg-card p-4 space-y-2">
          <h3 className="text-sm font-semibold">Order Summary</h3>
          {fee > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Card fee</span>
              <span>{formatCents(fee)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Card funding</span>
            <span>{formatCents(fundingAmountCents)}</span>
          </div>
          <div className="border-t pt-2 flex justify-between text-sm font-semibold">
            <span>Total</span>
            <span>{formatCents(fee + fundingAmountCents)}</span>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderDeliveryStep = () => (
    <Card className="max-w-lg mx-auto shadow-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="h-5 w-5" /> Card Delivery
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">How would you like to receive your card?</p>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className={`p-4 rounded-xl border-2 text-center transition-all ${deliveryType === "branch" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
            onClick={() => setDeliveryType("branch")}
          >
            <Building2 className="h-6 w-6 mx-auto mb-1 text-primary" />
            <span className="text-sm font-medium">Branch Pickup</span>
            <p className="text-xs text-muted-foreground mt-1">Collect from your branch</p>
          </button>
          <button
            type="button"
            className={`p-4 rounded-xl border-2 text-center transition-all ${deliveryType === "postal" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
            onClick={() => setDeliveryType("postal")}
          >
            <MapPin className="h-6 w-6 mx-auto mb-1 text-primary" />
            <span className="text-sm font-medium">Post to Address</span>
            <p className="text-xs text-muted-foreground mt-1">Delivered to your door</p>
          </button>
        </div>

        {deliveryType === "branch" && branches.length > 0 && (
          <div className="space-y-1.5">
            <Label>Select Branch</Label>
            <Select value={branchId || ""} onValueChange={(val) => setBranchId(val)}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a branch…" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    <span className="font-medium">{b.name}</span>
                    {b.address && <span className="text-muted-foreground ml-2 text-xs">— {b.address}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {deliveryType === "branch" && branches.length === 0 && (
          <div className="space-y-1.5">
            <Label>Branch Name or ID</Label>
            <Input
              value={branchId || ""}
              onChange={(e) => setBranchId(e.target.value)}
              placeholder="e.g. Main Street Branch or BR-001"
            />
            <p className="text-xs text-muted-foreground">Enter the branch where you'd like to collect your card.</p>
          </div>
        )}

        {deliveryType === "postal" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Full Name</Label>
              <Input value={`${firstName} ${lastName}`} disabled className="bg-muted/50" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Address Line 1</Label>
              <Input value={postalAddress.line1} onChange={(e) => setPostalAddress((p) => ({ ...p, line1: e.target.value }))} placeholder="123 Main Street" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Address Line 2 (optional)</Label>
              <Input value={postalAddress.line2 || ""} onChange={(e) => setPostalAddress((p) => ({ ...p, line2: e.target.value }))} placeholder="Apt, Suite, Floor" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={postalAddress.city} onChange={(e) => setPostalAddress((p) => ({ ...p, city: e.target.value }))} placeholder="New York" />
            </div>
            <div className="space-y-1.5">
              <Label>State / Province</Label>
              <Input value={postalAddress.state || ""} onChange={(e) => setPostalAddress((p) => ({ ...p, state: e.target.value }))} placeholder="NY" />
            </div>
            <div className="space-y-1.5">
              <Label>Postal Code</Label>
              <Input value={postalAddress.postalCode} onChange={(e) => setPostalAddress((p) => ({ ...p, postalCode: e.target.value }))} placeholder="10001" />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input value={postalAddress.country || ""} onChange={(e) => setPostalAddress((p) => ({ ...p, country: e.target.value }))} placeholder="United States" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderPreviewStep = () => (
    <div className="max-w-lg mx-auto space-y-4">
      <h2 className="text-xl font-bold font-display">Final Preview</h2>
      <CardMockPreview
        cardColorHsl={cardColor.hsl}
        orientation={orientation}
        cardLayout={tenant?.cardLayout}
        avatarUrl={useCutout && cutoutUrls[selectedAvatarId || "photo"] ? cutoutUrls[selectedAvatarId || "photo"] : selectedAvatar?.imageUrl}
        isCutout={useCutout && !!cutoutUrls[selectedAvatarId || "photo"]}
        avatarPosition={avatarPosition}
        avatarScale={avatarScale}
        firstName={firstName}
        lastName={lastName}
        tenantName={tenant?.brandName}
        cardNetwork={tenant?.cardNetwork} showChip={tenant?.showChip} showCardNetwork={tenant?.showCardNetwork}
        backgroundUrl={backgroundUrl}
        className="max-w-xs mx-auto"
      />
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{firstName} {lastName}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Color</span><span className="font-medium">{cardColor.name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Orientation</span><span className="capitalize font-medium">{orientation}</span></div>
          {!isWaitlist && fundingAmountCents && (
            <>
              <div className="border-t pt-2" />
              <div className="flex justify-between"><span className="text-muted-foreground">Card funding</span><span className="font-medium">{formatCents(fundingAmountCents)}</span></div>
              {feeUnavailable ? (
                <div className="flex justify-between"><span className="text-muted-foreground">Card fee</span><span className="font-medium text-destructive">Not available</span></div>
              ) : (
                <>
                  {(cardFeeCents as number) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Card fee</span><span className="font-medium">{formatCents(cardFeeCents as number)}</span></div>}
                  <div className="flex justify-between font-semibold"><span>Total</span><span>{formatCents((cardFeeCents as number) + fundingAmountCents)}</span></div>
                </>
              )}
            </>
          )}
          {!isWaitlist && postalAddress.line1 && (
            <>
              <div className="border-t pt-2" />
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="font-medium text-right text-xs">{postalAddress.line1}, {postalAddress.city} {postalAddress.postalCode}</span></div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const handleSaveCard = async () => {
    if (!tenant || !user) return;
    setSetupIntentLoading(true);
    try {
      let activeDraftId = draftCardRequestId;

      if (activeDraftId) {
        // Update existing draft with latest wizard data
        await supabase
          .from("card_requests")
          .update({
            first_name: firstName,
            last_name: lastName,
            card_color: cardColor.hsl,
            style: config.style || "cartoon",
            style_sub_choice: config.styleSubChoice || null,
            hobby: config.hobby || null,
            orientation,
            selected_avatar_image_id: selectedAvatarId || null,
            avatar_position: avatarPosition as any,
            avatar_scale: avatarScale,
            delivery_type: "postal",
            postal_address: postalAddress.line1 ? postalAddress as any : null,
            funding_amount_cents: fundingAmountCents,
            background_id: backgroundId,
          } as any)
          .eq("id", activeDraftId)
          .eq("user_id", user.id)
          .eq("tenant_id", tenant.id);
      } else {
        // Create a new draft card request BEFORE Stripe redirect
        const { id: draftId2, error: draftErr } = await cardRequestService.createClientCardRequest(
          tenant.id,
          "draft",
          {
            first_name: firstName,
            last_name: lastName,
            card_color: cardColor.hsl,
            style: config.style || "cartoon",
            style_sub_choice: config.styleSubChoice || null,
            hobby: config.hobby || null,
            orientation,
            selected_avatar_image_id: selectedAvatarId || null,
            avatar_position: avatarPosition,
            avatar_scale: avatarScale,
            delivery_type: "postal",
            postal_address: postalAddress.line1 ? postalAddress : null,
            funding_amount_cents: fundingAmountCents,
            background_id: backgroundId,
          }
        );

        if (draftErr || !draftId2) {
          console.error("Draft creation error:", draftErr);
          toast({ title: "Something went wrong", description: "Could not save card details.", variant: "destructive" });
          setSetupIntentLoading(false);
          return;
        }

        activeDraftId = draftId2;
        setDraftCardRequestId(draftId2);
        try { localStorage.setItem("cardmoji_draft_card_id", draftId2); } catch {}
      }

      const { data, error } = await supabase.functions.invoke("create-setup-intent", {
        body: { tenantId: tenant.id, tenantSlug: tenant_slug, draftCardRequestId: activeDraftId },
      });
      if (error || data?.error) {
        toast({ title: "Something went wrong", description: data?.error || "Could not start card setup", variant: "destructive" });
        setSetupIntentLoading(false);
        return;
      }
      if (data?.url) {
        const inIframe = window.self !== window.top;
        if (inIframe) {
          window.open(data.url, "_blank");
          toast({ title: "Checkout opened", description: "Complete your card setup in the new tab." });
          setSetupIntentLoading(false);
        } else {
          window.location.href = data.url;
        }
      }
    } catch (err) {
      console.error("SetupIntent error:", err);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
      setSetupIntentLoading(false);
    }
  };

  const renderSaveCardStep = () => {
    if (!user) {
      return renderSubmitStep(); // Redirect to auth first
    }

    if (setupIntentDone) {
      return (
        <div className="max-w-lg mx-auto flex flex-col items-center gap-6 text-center py-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <Check className="h-8 w-8 text-success" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-display mb-2">Card Saved!</h2>
            <p className="text-muted-foreground text-sm">
              Your payment method is securely saved. You won't be charged until the waitlist goal is reached.
            </p>
          </div>
          <Button
            className="gradient-primary text-primary-foreground font-semibold gap-2 px-8"
            size="lg"
            onClick={() => setStep(step + 1)}
          >
            Continue to Join Waitlist <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (feeUnavailable) return feeNotConfigured;
    return (
      <div className="max-w-lg mx-auto flex flex-col items-center gap-6 text-center py-8">
        <CreditCard className="h-12 w-12 text-primary" />
        <div>
          <h2 className="text-xl font-bold font-display mb-2">Save Your Payment Details</h2>
          <p className="text-muted-foreground text-sm max-w-sm">
            You'll be taken to Stripe's secure checkout to save your card. <strong>You will not be charged</strong> until your card is ready to ship.
          </p>
        </div>

        <Card className="w-full shadow-card">
          <CardContent className="p-4 space-y-2 text-sm">
            {(cardFeeCents as number) > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Card issuance fee</span>
                <span>{formatCents(cardFeeCents as number)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Card funding</span>
              <span>{formatCents(fundingAmountCents || 0)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Charged when card ships</span>
              <span>{formatCents((cardFeeCents as number) + (fundingAmountCents || 0))}</span>
            </div>
          </CardContent>
        </Card>

        <div className="w-full max-w-sm rounded-xl border border-primary/20 bg-primary/5 p-4 text-left space-y-1.5">
          <p className="text-xs font-medium flex items-center gap-1.5">
            <Lock className="h-3 w-3 text-primary" /> No charge today
          </p>
          <p className="text-xs text-muted-foreground">
            Your card details are encrypted and stored securely by Stripe. You'll only be charged once your custom card is approved and ready to print.
          </p>
        </div>

        <Button
          className="gradient-primary text-primary-foreground font-semibold gap-2 px-8"
          size="lg"
          onClick={handleSaveCard}
          disabled={setupIntentLoading}
        >
          {setupIntentLoading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to Stripe...</>
          ) : (
            <>Continue to Stripe <ArrowRight className="h-4 w-4" /></>
          )}
        </Button>
      </div>
    );
  };


  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    if (authMode === "signup") {
      const { error } = await signUp(authEmail, authPassword);
      if (error) {
        setAuthError(error);
      } else {
        setEmailSent(true);
      }
    } else {
      const { error } = await signIn(authEmail, authPassword);
      if (error) {
        setAuthError(error);
      }
    }
    setAuthLoading(false);
  };

  const handleMagicLink = async () => {
    if (!authEmail) return;
    setAuthError(null);
    setAuthLoading(true);
    const { error } = await signInWithMagicLink(authEmail);
    if (error) {
      setAuthError(error);
    } else {
      setEmailSent(true);
    }
    setAuthLoading(false);
  };

  const renderBankSubmitStep = () => (
    <div className="max-w-lg mx-auto flex flex-col items-center gap-6 text-center py-8">
      <Send className="h-12 w-12 text-primary" />
      <div>
        <h2 className="text-xl font-bold font-display mb-2">Submit for Review</h2>
        <p className="text-muted-foreground text-sm">
          Your personalised card design will be sent to {tenant?.brandName || "the bank"} for approval. You'll be notified once it's reviewed.
        </p>
      </div>

      <Card className="w-full shadow-card">
        <CardContent className="p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{firstName} {lastName}</span></div>
          {panLast5 && <div className="flex justify-between"><span className="text-muted-foreground">PAN (last digits)</span><span className="font-medium font-mono">•••• {panLast5}</span></div>}
          <div className="flex justify-between"><span className="text-muted-foreground">Color</span><span className="font-medium">{cardColor.name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Orientation</span><span className="capitalize font-medium">{orientation}</span></div>
          <div className="border-t pt-2" />
          <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="capitalize font-medium">{deliveryType === "branch" ? "Branch Pickup" : "Post"}</span></div>
          {deliveryType === "branch" && branchId && (
            <div className="flex justify-between"><span className="text-muted-foreground">Branch</span><span className="font-medium">{branches.find(b => b.id === branchId)?.name || branchId}</span></div>
          )}
          {deliveryType === "postal" && postalAddress.line1 && (
            <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium text-right text-xs">{postalAddress.line1}, {postalAddress.city} {postalAddress.postalCode}</span></div>
          )}
        </CardContent>
      </Card>

      <CardMockPreview
        cardColorHsl={cardColor.hsl}
        orientation={orientation}
        cardLayout={tenant?.cardLayout}
        avatarUrl={useCutout && cutoutUrls[selectedAvatarId || "photo"] ? cutoutUrls[selectedAvatarId || "photo"] : selectedAvatar?.imageUrl}
        isCutout={useCutout && !!cutoutUrls[selectedAvatarId || "photo"]}
        avatarPosition={avatarPosition}
        avatarScale={avatarScale}
        firstName={firstName}
        lastName={lastName}
        tenantName={tenant?.brandName}
        cardNetwork={tenant?.cardNetwork} showChip={tenant?.showChip} showCardNetwork={tenant?.showCardNetwork}
        backgroundUrl={backgroundUrl}
        className="max-w-[200px] mx-auto"
      />

      <Button
        className="gradient-primary text-primary-foreground font-semibold gap-2 px-8"
        size="lg"
        onClick={handleBankSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
        ) : (
          <>Submit for Review <Send className="h-4 w-4" /></>
        )}
      </Button>
    </div>
  );

  const renderSubmitStep = () => {
    // If user isn't authenticated, show sign-up/sign-in form
    if (!user) {
      if (emailSent) {
        return (
          <div className="max-w-lg mx-auto flex flex-col items-center gap-4 text-center py-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <Mail className="h-8 w-8 text-success" />
            </div>
            <h2 className="text-xl font-bold font-display">Check your email!</h2>
            <p className="text-sm text-muted-foreground max-w-xs">
              We sent a {authMode === "signup" ? "verification" : "sign-in"} link to <strong>{authEmail}</strong>. Click it to continue.
            </p>
            <Button variant="outline" size="sm" onClick={() => setEmailSent(false)}>
              Use a different email
            </Button>
          </div>
        );
      }

      return (
        <div className="max-w-sm mx-auto space-y-6 py-8">
          <div className="text-center space-y-2">
            <Users className="h-10 w-10 text-primary mx-auto" />
            <h2 className="text-xl font-bold font-display">
              {authMode === "signup" ? "Create Your Account" : "Welcome Back"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {authMode === "signup"
                ? "Sign up to join the waitlist and track your card."
                : "Sign in to rejoin the waitlist."}
            </p>
          </div>

          <form onSubmit={handleAuth} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email"
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth-password">Password</Label>
              <div className="relative">
                <Input
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {authError && (
              <p className="text-sm text-destructive">{authError}</p>
            )}

            <Button
              type="submit"
              className="w-full gradient-primary text-primary-foreground font-semibold"
              disabled={authLoading}
            >
              {authLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Please wait...</>
              ) : authMode === "signup" ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">or</span></div>
          </div>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={handleMagicLink}
            disabled={authLoading || !authEmail}
          >
            <Mail className="h-4 w-4" /> Send magic link
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {authMode === "signup" ? (
              <>Already have an account?{" "}
              <button className="text-primary font-medium hover:underline" onClick={() => { setAuthMode("signin"); setAuthError(null); }}>
                Sign in
              </button></>
            ) : (
              <>Don't have an account?{" "}
              <button className="text-primary font-medium hover:underline" onClick={() => { setAuthMode("signup"); setAuthError(null); }}>
                Sign up
              </button></>
            )}
          </p>
        </div>
      );
    }

    // Authenticated — show the join waitlist UI
    return (
      <div className="max-w-lg mx-auto flex flex-col items-center gap-6 text-center py-8">
        <Users className="h-12 w-12 text-primary" />
        <div>
          <h2 className="text-xl font-bold font-display mb-2">Join the Waitlist?</h2>
          <p className="text-muted-foreground text-sm">
            Your card design will be saved and you'll be added to the {tenant?.brandName || ""} waitlist. You won't be charged until the goal is reached.
          </p>
        </div>

        {/* Referral code input */}
        <div className="w-full max-w-xs space-y-2">
          <Label htmlFor="referral" className="text-sm flex items-center gap-1.5 justify-center">
            <Gift className="h-4 w-4 text-primary" /> Have a referral code?
          </Label>
          <Input
            id="referral"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="e.g. A1B2C3"
            className="text-center font-mono tracking-widest uppercase"
            maxLength={6}
          />
          {/* NO TYPED REWARD. The old "$5" described a payout scheme that never existed; the
              configured reward is a fee discount, resolved from config where it is stated. */}
              <p className="text-xs text-muted-foreground">Optional — your friend moves up the list.</p>
        </div>

        <Button
          className="gradient-primary text-primary-foreground font-semibold gap-2 px-8"
          size="lg"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Joining..." : "Join Waitlist"}
          <Users className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const lastStep = STEPS.length - 1;
  const currentStepName = STEPS[step];
  const isTerminalStep = currentStepName === "Submit" || currentStepName === "Join Waitlist";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate(`/t/${tenant_slug}/editor`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">
          {isWaitlist ? "Join Waitlist" : "Submit Your Card"}
        </span>
      </div>

      <WizardProgress steps={STEPS} currentStep={step} />

      <div className="flex-1 px-4 pb-28 animate-fade-in">
        {renderStep()}
      </div>

      {/* Hide bottom bar on terminal steps and Save Card step (when not done) */}
      {!isTerminalStep && step < lastStep && !(currentStepName === "Save Card" && !setupIntentDone) && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[960px] bg-background/80 backdrop-blur border-t p-4">
          <div className="max-w-lg mx-auto flex gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            <Button
              className="flex-1 gradient-primary text-primary-foreground font-semibold gap-2"
              disabled={!canProceed()}
              onClick={() => setStep(step + 1)}
            >
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionFlow;
